import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Link,
  Stack,
  Table,
  type TableColumn,
  useToast,
} from "@knowtex-ai/components";
import { Play } from "iconoir-react";
import { MC_RUNS_LIMIT, type McRun } from "../../lib/mc";
import {
  useMcRunsQuery,
  useMcTargetPlaybooksQuery,
} from "../../hooks/useMcRuns";
import McHeader from "./McHeader";
import McPageShell from "./McPageShell";
import { GAP } from "./layout";
import { RunResult, RunStatusBadge } from "./RunBadges";
import { RunTriggerCell } from "./RunTrigger";
import StartRunModal from "./StartRunModal";
import TimeCell from "./TimeCell";
import {
  durationMs,
  formatCost,
  formatDuration,
  formatTrigger,
  runPlaybook,
  runResult,
} from "./format";

interface McRunsPageProps {
  setAuth: (isAuthenticated: boolean) => void;
  setUserOrg: (org: string | null) => void;
}

export default function McRunsPage({ setAuth, setUserOrg }: McRunsPageProps) {
  return (
    <McPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <McRuns />
    </McPageShell>
  );
}

function McRuns() {
  const navigate = useNavigate();
  const toast = useToast();
  const runsQuery = useMcRunsQuery();
  const playbooks = useMcTargetPlaybooksQuery().data;
  const [startOpen, setStartOpen] = useState(false);
  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);

  const columns = useMemo(
    (): TableColumn<McRun>[] => [
      {
        id: "run",
        header: "Run ID & playbook",
        width: 200,
        grow: true,
        accessor: (run) => runPlaybook(run.input, playbooks ?? []).title,
        cell: (run) => {
          const playbook = runPlaybook(run.input, playbooks ?? []).title;
          return (
            <div className="min-w-0">
              <Link
                to={`/mc/runs/${run.id}`}
                aria-label={`Open MC run ${run.id}`}
              >
                <span className="font-mono">{run.id.slice(0, 8)}</span>
              </Link>
              <div
                className="truncate text-[13px] text-gray-500"
                title={playbook}
              >
                {playbook}
              </div>
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        width: 130,
        accessor: "status",
        sortable: true,
        cell: (run) => <RunStatusBadge status={run.status} />,
      },
      {
        id: "result",
        header: "Result",
        width: 160,
        accessor: (run) => runResult(run)?.label,
        sortable: true,
        cell: (run) => <RunResult run={run} />,
      },
      {
        id: "trigger",
        header: "Trigger",
        width: 150,
        accessor: formatTrigger,
        cell: (run) => <RunTriggerCell run={run} />,
      },
      {
        id: "started",
        header: "Started",
        width: 240,
        accessor: (run) => new Date(run.startedAt || run.createdAt),
        sortable: true,
        cell: (run) => <TimeCell iso={run.startedAt || run.createdAt} />,
      },
      {
        id: "duration",
        header: "Duration",
        width: 112,
        align: "end",
        accessor: (run) => durationMs(run),
        sortable: true,
        cell: (run) => formatDuration(run),
      },
      {
        id: "cost",
        header: "Cost",
        width: 92,
        align: "end",
        accessor: "costUsd",
        sortable: true,
        cell: formatCost,
      },
    ],
    [playbooks],
  );

  return (
    <>
      <McHeader
        section="runs"
        actions={
          <Button icon={Play} onClick={() => setStartOpen(true)}>
            Trigger run
          </Button>
        }
      />

      <div className="mt-8">
        <Stack gap={GAP.page}>
          {runsQuery.isError && (
            <Banner variant="error" heading="Couldn't load MC runs">
              {runsQuery.error.message}
            </Banner>
          )}
          {runs.length >= MC_RUNS_LIMIT && (
            <Banner variant="info">
              Showing the latest {MC_RUNS_LIMIT} runs.
            </Banner>
          )}
          <Table
            aria-label="MC runs"
            columns={columns}
            data={runs}
            getRowId={(run) => run.id}
            loading={runsQuery.isLoading}
            defaultPageSize={10}
            emptyState={
              runsQuery.isError
                ? "Runs couldn't be loaded."
                : "No runs yet. Trigger one to see it here."
            }
          />
        </Stack>
      </div>

      <StartRunModal
        open={startOpen}
        onOpenChange={setStartOpen}
        onStarted={(run) => {
          setStartOpen(false);
          toast.success("MC run started", "Opening the run.");
          navigate(`/mc/runs/${run.id}`);
        }}
      />
    </>
  );
}
